import { Link } from 'react-router-dom';
import cn from 'classnames';
import { Button, EditableText, Skeleton, lucideIcons, toast } from '@round/creatorbase-ui';
import { CREATORBASE_ROOT_PATH } from 'Modules/Navigation/whitelabel/routes';
import { usePoolDetail } from '../../hooks/usePoolDetail';
import { useUpdatePoolInfo } from '../../hooks/useUpdatePoolInfo';
import styles from './PoolInfo.module.css';

const { ArrowLeft } = lucideIcons;

const NAME_PLACEHOLDER = 'Untitled pool';
const DESCRIPTION_PLACEHOLDER = 'Add a description';
const BACK_TO_LIST_LABEL = 'Back to creator pools';

/**
 * Where the way back leads. Absolute, like every other route in the app: react-router is on 6.3, which
 * resolves a relative `..` against the route tree rather than the path, and the detail route sits
 * directly under pathless layout routes — so `..` would land on `/`.
 */
const POOLS_LIST_PATH = `/${CREATORBASE_ROOT_PATH}/pools/`;

/** Offered in every state, including the ones where there is no pool to read. */
const BackToListButton = () => (
    <Button asChild variant="ghost" size="icon-sm" className={styles.backButton} aria-label={BACK_TO_LIST_LABEL}>
        <Link to={POOLS_LIST_PATH}>
            <ArrowLeft />
        </Link>
    </Button>
);

type Props = {
    poolId: number | null | undefined;
};

export const PoolInfo = ({ poolId }: Props) => {
    const { pool, isLoading, errorMessage } = usePoolDetail(poolId);
    const { update } = useUpdatePoolInfo(poolId);

    const handleNameCommit = (name: string) => {
        if (name.trim() === '') {
            toast.error("Pool name can't be empty");
            return;
        }

        if (typeof poolId !== 'number') {
            return;
        }

        update({ id: poolId, name });
    };

    const handleDescriptionCommit = (description: string) => {
        if (typeof poolId !== 'number') {
            return;
        }

        update({ id: poolId, description });
    };

    if (isLoading) {
        return (
            <div className={styles.info}>
                <BackToListButton />
                <div className={styles.text}>
                    <Skeleton className={styles.nameSkeleton} />
                    <Skeleton className={styles.descriptionSkeleton} />
                </div>
            </div>
        );
    }

    if (!pool) {
        return (
            <div className={styles.info}>
                <BackToListButton />
                <p className={styles.error}>{errorMessage ?? "Couldn't load pool"}</p>
            </div>
        );
    }

    return (
        <div className={styles.info}>
            <BackToListButton />
            <div className={styles.text}>
                <EditableText
                    value={pool.name}
                    onChange={handleNameCommit}
                    placeholder={NAME_PLACEHOLDER}
                    ariaLabel="Pool name"
                    displayClassName={styles.name}
                    editClassName={cn(styles.name, styles.editField)}
                />
                <EditableText
                    multiline
                    value={pool.description}
                    onChange={handleDescriptionCommit}
                    placeholder={DESCRIPTION_PLACEHOLDER}
                    ariaLabel="Pool description"
                    displayClassName={styles.description}
                    editClassName={cn(styles.description, styles.editField)}
                />
            </div>
        </div>
    );
};
